import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { pointerEventsStyles, COLORS, ELEVATIONS, RADIUS } from '@garnish/constants';

import { Icon } from '../../Icon';

export const ButtonZoom = (props: ButtonZoomProps) => {
  const { zoomInProps, zoomOutProps, style, withTransparentBackground, ...wrapperProps } = props;
  const {
    onPress: onZoomInPress,
    disabled: isZoomInDisabled,
    accessibilityLabel: zoomInAccessibilityLabel = BUTTON_PROPS_BY_TYPE.zoomIn.accessibilityLabel,
    accessibilityHint: zoomInAccessibilityHint = BUTTON_PROPS_BY_TYPE.zoomIn.accessibilityHint,
  } = zoomInProps ?? {};
  const {
    onPress: onZoomOutPress,
    disabled: isZoomOutDisabled,
    accessibilityLabel: zoomOutAccessibilityLabel = BUTTON_PROPS_BY_TYPE.zoomOut.accessibilityLabel,
    accessibilityHint: zoomOutAccessibilityHint = BUTTON_PROPS_BY_TYPE.zoomOut.accessibilityHint,
  } = zoomOutProps ?? {};

  const buttonBackgroundStyle = withTransparentBackground
    ? styles.buttonTransparent
    : styles.buttonBackground;
  const wrapperStyles = [
    styles.wrapper,
    withTransparentBackground ? undefined : styles.wrapperLegacy,
    style,
  ];
  const zoomInIconColor = isZoomInDisabled ? COLORS.OPACITY.DARK_KALE.LIGHT : COLORS.TEXT_COLOR;
  const zoomOutIconColor = isZoomOutDisabled ? COLORS.OPACITY.DARK_KALE.LIGHT : COLORS.TEXT_COLOR;

  return (
    <View {...wrapperProps} style={wrapperStyles}>
      <TouchableOpacity
        activeOpacity={ACTIVE_OPACITY}
        accessibilityRole="button"
        accessibilityLabel={zoomInAccessibilityLabel}
        accessibilityHint={zoomInAccessibilityHint}
        accessibilityState={{ disabled: Boolean(isZoomInDisabled), busy: false }}
        style={[styles.button, styles.buttonZoomIn, buttonBackgroundStyle]}
        disabled={isZoomInDisabled}
        onPress={onZoomInPress}
      >
        <Icon
          name={BUTTON_PROPS_BY_TYPE.zoomIn.centerIcon}
          width={ICON_SIZE}
          height={ICON_SIZE}
          color={zoomInIconColor}
          style={pointerEventsStyles.none}
        />
      </TouchableOpacity>

      <View style={styles.separator} />

      <TouchableOpacity
        activeOpacity={ACTIVE_OPACITY}
        accessibilityRole="button"
        accessibilityLabel={zoomOutAccessibilityLabel}
        accessibilityHint={zoomOutAccessibilityHint}
        accessibilityState={{ disabled: Boolean(isZoomOutDisabled), busy: false }}
        style={[styles.button, styles.buttonZoomOut, buttonBackgroundStyle]}
        disabled={isZoomOutDisabled}
        onPress={onZoomOutPress}
      >
        <Icon
          name={BUTTON_PROPS_BY_TYPE.zoomOut.centerIcon}
          width={ICON_SIZE}
          height={ICON_SIZE}
          color={zoomOutIconColor}
          style={pointerEventsStyles.none}
        />
      </TouchableOpacity>
    </View>
  );
};

export const BUTTON_PROPS_BY_TYPE = {
  zoomIn: {
    centerIcon: 'IconPlus2',
    accessibilityLabel: 'Zoom In',
    accessibilityHint: 'Increase zoom level',
  },
  zoomOut: {
    centerIcon: 'IconMinus2',
    accessibilityLabel: 'Zoom Out',
    accessibilityHint: 'Decrease zoom level',
  },
} as const;

//
// ─── CONSTANTS ──────────────────────────────────────────────────────────────────
//

const ACTIVE_OPACITY = 0.8;
const ICON_SIZE = 24;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  wrapper: {
    width: 40,
    borderRadius: RADIUS.medium,
  },
  wrapperLegacy: {
    ...ELEVATIONS['8'],
  },
  separator: {
    height: 1,
    backgroundColor: COLORS.NEUTRAL_3,
  },
  button: {
    height: 40,
    borderRadius: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonBackground: {
    backgroundColor: COLORS.CREAM,
  },
  buttonTransparent: {
    backgroundColor: COLORS.OPACITY.TRANSPARENT,
  },
  buttonZoomIn: {
    borderTopStartRadius: RADIUS.medium,
    borderTopEndRadius: RADIUS.medium,
    borderBottomStartRadius: 0,
    borderBottomEndRadius: 0,
  },
  buttonZoomOut: {
    borderTopStartRadius: 0,
    borderTopEndRadius: 0,
    borderBottomStartRadius: RADIUS.medium,
    borderBottomEndRadius: RADIUS.medium,
  },
});

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type ButtonZoomProps = {
  zoomInProps?: ZoomButtonProps;
  zoomOutProps?: ZoomButtonProps;
  withTransparentBackground?: boolean;
} & ViewProps;
type ZoomButtonProps = {
  onPress?: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  accessibilityHint?: string;
};
